import { eegBandPowers, type BandName } from './bandPowers';
import { cognitiveIndices } from './eegIndices';
import { handIndices } from './handIndices';
import { ensureFusionStreams } from './imuFusion';
import { TailScope, feedScope } from './scope';
import { computeTrajectories } from './trajectory';
import {
  TRAIT_KEYS,
  rawToPercent,
  traitRaw,
  type TraitKey,
} from './traitScoring';
import type { ParsedSession, SensorSample, SessionManifest } from './types';

/**
 * Pre-compute the full replay timeline from parsed samples, running the
 * capture-app's own DSP at the same cadence the live recording screen
 * uses (band powers every 500 ms, cognitive + hand traits every 1 s).
 * Pre-computing once makes scrubbing instant — playback just indexes by
 * elapsed time.
 *
 * Mirrors capture-app GuidedPhaseStep's tick logic:
 *   - eegBandPowers(scope, EEG_KEYS) every 500 ms
 *   - cognitiveIndices(scope, EEG_KEYS) + handIndices(scope, HANDS, 50)
 *     every 1 s, fed through traitScoring.rawToPercent.
 */

const EEG_KEYS = ['eeg:TP9', 'eeg:AF7', 'eeg:AF8', 'eeg:TP10'];
const HAND_SENSORS = ['metawear-left', 'metawear-right'];
const ACCEL_SAMPLE_RATE = 50;

const ACCEL_TICK_MS = 40; // 25 Hz — smooth motion trace
const BAND_TICK_MS = 500; // matches live band chart
const TRAIT_TICK_MS = 1000; // matches live trait bars

// Scope only needs to hold the longest DSP window (256 EEG samples);
// 512 leaves comfortable headroom for both EEG and accel channels.
const SCOPE_CAPACITY = 512;

export interface BandFrame {
  /** Elapsed seconds from session start. */
  t: number;
  delta: number;
  theta: number;
  alpha: number;
  beta: number;
  gamma: number;
}

export interface TraitFrame {
  t: number;
  /** Per-trait 0–100 percent (null until that trait's DSP is warm). */
  focus: number | null;
  stress: number | null;
  steady: number | null;
  tremor: number | null;
  fatigue: number | null;
  smoothness: number | null;
}

export interface AccelFrame {
  t: number;
  left: number | null;
  right: number | null;
}

export interface PhaseSpan {
  phase: string;
  startS: number;
  endS: number;
}

/** One reconstructed trajectory point, time in elapsed seconds. */
export interface TrajPointS {
  t: number;
  x: number;
  y: number;
  z: number;
}

export interface HandPath {
  /** 'metawear-left' | 'metawear-right'. */
  sensorId: string;
  points: TrajPointS[];
}

export interface ReplayTimeline {
  durationS: number;
  bands: BandFrame[];
  traits: TraitFrame[];
  accel: AccelFrame[];
  phases: PhaseSpan[];
  /** Per-hand reconstructed 3D position paths (host-fusion sessions). */
  trajectories: HandPath[];
  hasEeg: boolean;
  hasImu: boolean;
  /** True when at least one hand has a drawable (>1 point) path. */
  hasTrajectory: boolean;
  /** Set by the streaming builder when the recording is too long to
   *  rebuild the 3D hand path (see streamTimeline.ts). */
  trajectorySkipped?: boolean;
}

function buildTraitFrame(t: number, scope: TailScope): TraitFrame {
  const cog = cognitiveIndices(scope, EEG_KEYS);
  const hand = handIndices(scope, HAND_SENSORS, ACCEL_SAMPLE_RATE);
  const pct: Record<TraitKey, number | null> = {
    focus: null,
    stress: null,
    steady: null,
    tremor: null,
    fatigue: null,
    smoothness: null,
  };
  for (const key of TRAIT_KEYS) {
    const raw = traitRaw(key, cog, hand);
    pct[key] = raw === null ? null : rawToPercent(key, raw);
  }
  return { t, ...pct };
}

/**
 * Re-anchor per-sensor clocks when they're skewed.
 *
 * Muse and MetaWear can report `hostTime` on different clock bases — a
 * ~166 s skew was observed on a Stanford capture — so the global
 * (max − min) span balloons (e.g. 224 s for a ~58 s recording). The
 * replay scales the seek bar against that span, so the playhead crawls
 * and one sensor's samples land far off the timeline. When the union
 * span dwarfs the widest single sensor's own span, that's a clock-base
 * skew (not a real gap) — re-anchor each sensor's first sample to t=0 so
 * the streams replay concurrently from the start. Single-clock
 * recordings (the normal case) are returned untouched.
 */
function realignSensorClocks(samples: SensorSample[]): SensorSample[] {
  if (samples.length === 0) return samples;
  const minBy = new Map<string, number>();
  const maxBy = new Map<string, number>();
  let gMin = Infinity;
  let gMax = -Infinity;
  for (const s of samples) {
    const mn = minBy.get(s.sensorId);
    if (mn === undefined || s.hostTime < mn) minBy.set(s.sensorId, s.hostTime);
    const mx = maxBy.get(s.sensorId);
    if (mx === undefined || s.hostTime > mx) maxBy.set(s.sensorId, s.hostTime);
    if (s.hostTime < gMin) gMin = s.hostTime;
    if (s.hostTime > gMax) gMax = s.hostTime;
  }
  let widestSensorSpan = 0;
  for (const [id, mn] of minBy) {
    widestSensorSpan = Math.max(widestSensorSpan, (maxBy.get(id) ?? mn) - mn);
  }
  // > 5 s slack absorbs genuine small start staggers; real clock skew is
  // tens-to-hundreds of seconds. Needs ≥ 2 sensors to be meaningful.
  const skewed =
    minBy.size > 1 && gMax - gMin > widestSensorSpan + 5000;
  if (!skewed) return samples;
  return samples
    .map((s) => ({ ...s, hostTime: s.hostTime - (minBy.get(s.sensorId) ?? 0) }))
    .sort((a, b) => a.hostTime - b.hostTime);
}

export function derivePhases(
  manifest: SessionManifest | null,
  samples: SensorSample[],
  t0: number,
  durationS: number,
): PhaseSpan[] {
  // Prefer the manifest's phase segments (authoritative start/end).
  const manifestPhases = manifest?.phases;
  if (manifestPhases && manifestPhases.length > 0) {
    const spans: PhaseSpan[] = [];
    for (const p of manifestPhases) {
      const startMs = Date.parse(p.startedAt);
      const endMs = Date.parse(p.endedAt);
      if (Number.isFinite(startMs) && Number.isFinite(endMs)) {
        spans.push({
          phase: p.phase,
          startS: Math.max(0, (startMs - t0) / 1000),
          endS: Math.max(0, (endMs - t0) / 1000),
        });
      }
    }
    // After per-sensor clock re-anchoring the manifest's wall-clock phase
    // times no longer map onto the (now relative) sample clock — keep only
    // spans that actually land on the timeline, else fall back to tags.
    const valid = spans.filter((s) => s.endS > 0 && s.startS < durationS);
    if (valid.length > 0) return valid;
  }
  // Fall back to transitions in the per-sample `phase` tag.
  const spans: PhaseSpan[] = [];
  let current: { phase: string; startS: number } | null = null;
  for (const s of samples) {
    const phase = s.phase ?? 'unknown';
    const tS = (s.hostTime - t0) / 1000;
    if (current === null) {
      current = { phase, startS: tS };
    } else if (phase !== current.phase) {
      spans.push({ phase: current.phase, startS: current.startS, endS: tS });
      current = { phase, startS: tS };
    }
  }
  if (current) spans.push({ ...current, endS: durationS });
  return spans;
}

export interface TickFrames {
  bands: BandFrame[];
  traits: TraitFrame[];
  accel: AccelFrame[];
  hasEeg: boolean;
  hasImu: boolean;
}

/**
 * The replay's tick engine: feed time-ordered samples, emit band powers
 * every 500 ms, traits every 1 s and the hand-motion trace at 25 Hz — the
 * live recording screen's cadence. Shared by `buildTimeline` (whole file
 * in memory) and the streaming builder (`streamTimeline.ts`), so both run
 * the identical maths. `samples` may be a generator: nothing is kept.
 */
export function runTicks(
  samples: Iterable<SensorSample>,
  t0: number,
  tEnd: number,
): TickFrames {
  let hasEeg = false;
  let hasImu = false;

  const scope = new TailScope(SCOPE_CAPACITY);
  const bands: BandFrame[] = [];
  const traits: TraitFrame[] = [];
  const accel: AccelFrame[] = [];

  let nextAccel = t0 + ACCEL_TICK_MS;
  let nextBand = t0 + BAND_TICK_MS;
  let nextTrait = t0 + TRAIT_TICK_MS;

  const emitAccel = (atMs: number) => {
    accel.push({
      t: (atMs - t0) / 1000,
      left: scope.latest('accel:metawear-left'),
      right: scope.latest('accel:metawear-right'),
    });
  };
  const emitBand = (atMs: number) => {
    const bp = eegBandPowers(scope, EEG_KEYS);
    if (bp) {
      bands.push({ t: (atMs - t0) / 1000, ...(bp as Record<BandName, number>) });
    }
  };
  const emitTrait = (atMs: number) => {
    traits.push(buildTraitFrame((atMs - t0) / 1000, scope));
  };

  for (const s of samples) {
    if (s.stream.startsWith('eeg:')) hasEeg = true;
    else if (
      s.stream === 'accelerometer' ||
      s.stream === 'linearAcceleration'
    ) {
      hasImu = true;
    }
    // Emit any ticks whose time has arrived, computed from the scope
    // state BEFORE this sample (window ending at the tick contains
    // only samples < tick time) — same as the live timer cadence.
    while (s.hostTime >= nextAccel) {
      emitAccel(nextAccel);
      nextAccel += ACCEL_TICK_MS;
    }
    while (s.hostTime >= nextBand) {
      emitBand(nextBand);
      nextBand += BAND_TICK_MS;
    }
    while (s.hostTime >= nextTrait) {
      emitTrait(nextTrait);
      nextTrait += TRAIT_TICK_MS;
    }
    feedScope(scope, s);
  }
  // Flush a final tick at the end so the last second is represented.
  emitAccel(tEnd);
  emitBand(tEnd);
  emitTrait(tEnd);
  return { bands, traits, accel, hasEeg, hasImu };
}

export function buildTimeline(parsed: ParsedSession): ReplayTimeline {
  // Correct cross-sensor clock skew up front (Muse vs MetaWear); no-op
  // for normal single-clock recordings.
  const samples = realignSensorClocks(parsed.samples);
  if (samples.length === 0) {
    return {
      durationS: 0,
      bands: [],
      traits: [],
      accel: [],
      phases: [],
      trajectories: [],
      hasEeg: false,
      hasImu: false,
      hasTrajectory: false,
    };
  }

  const t0 = samples[0].hostTime;
  const tEnd = samples[samples.length - 1].hostTime;
  const durationS = Math.max(0, (tEnd - t0) / 1000);
  const { bands, traits, accel, hasEeg, hasImu } = runTicks(samples, t0, tEnd);

  // Reconstructed 3D hand paths. For raw ('off') recordings the fusion
  // (quaternion + linearAcceleration) is synthesized in-browser from raw
  // accel+gyro (ensureFusionStreams — mirrors the server pre-pass); host /
  // legacy recordings already carry those streams and pass through. Convert
  // absolute host-time to elapsed seconds so the replay reveals the path.
  const trajectories: HandPath[] = computeTrajectories(
    ensureFusionStreams(samples),
  ).map((h) => ({
    sensorId: h.sensorId,
    points: h.points.map((p) => ({
      t: (p.t - t0) / 1000,
      x: p.x,
      y: p.y,
      z: p.z,
    })),
  }));
  const hasTrajectory = trajectories.some((h) => h.points.length > 1);

  return {
    durationS,
    bands,
    traits,
    accel,
    phases: derivePhases(parsed.manifest ?? null, samples, t0, durationS),
    trajectories,
    hasEeg,
    hasImu,
    hasTrajectory,
  };
}
